import { useState } from 'react';
import { UserCog } from 'lucide-react';
import type { Role } from '@parkline/shared';
import { ROLE_LABELS } from '../lib/shared';

import { useAuth } from '../lib/auth';

/**
 * Переключатель роли для того, у кого их несколько.
 *
 * Смена роли без пароля — не поблажка, а условие того, что ею вообще будут пользоваться:
 * механик, который днём подменяет медработника, не станет выходить и логиниться заново
 * посреди смены. Он просто продолжит работать в прежней роли, и разделение окажется
 * бесполезным.
 *
 * У кого роль одна — не показываем ничего: лишний элемент в шапке заставляет думать, что
 * где-то есть выбор, которого нет.
 */
export function RoleSwitch() {
  const { user, roles, switchRole } = useAuth();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState<Role | null>(null);
  const [error, setError] = useState<string | null>(null);

  const available = user?.availableRoles ?? [];
  if (available.length < 2) {
    return null;
  }

  const current = roles[0];

  const pick = async (role: Role) => {
    if (role === current) {
      setOpen(false);
      return;
    }
    setError(null);
    setBusy(role);
    try {
      await switchRole(role);
      setOpen(false);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Не удалось сменить роль');
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        className="flex items-center gap-2 rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-700 transition-colors hover:bg-slate-100"
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <UserCog className="h-4 w-4" aria-hidden />
        <span className="hidden sm:inline">Сменить роль</span>
      </button>

      {open ? (
        <div
          role="menu"
          className="absolute right-0 z-20 mt-2 w-64 rounded-lg border border-slate-200 bg-white p-2 shadow-lg"
        >
          <p className="px-2 pb-1 text-xs text-slate-500">Работаю как</p>
          {available.map((role) => (
            <button
              key={role}
              type="button"
              role="menuitem"
              disabled={busy !== null}
              onClick={() => void pick(role)}
              className={`flex w-full items-center justify-between rounded px-2 py-2 text-left text-sm transition-colors hover:bg-slate-100 ${
                role === current ? 'font-semibold text-brand-800' : 'text-slate-700'
              }`}
            >
              <span>{ROLE_LABELS[role] ?? role}</span>
              {role === current ? <span className="text-xs text-slate-400">сейчас</span> : null}
            </button>
          ))}
          {error ? (
            <p className="px-2 pt-1 text-xs text-red-700" role="alert">
              {error}
            </p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
